// Ported from design/project/App21b-ChallengeLive.dc.html, then wired: the challenge waits here for a taker. Sending
// the link from another app is fine; Back or closing the app cancels it. Accepted, the battle event takes the player
// straight into the battle; run out, it says the stake is back.
import { useEffect, useState } from "react";
import { ApiError } from "../api.js";
import { useGame, useTicker } from "../game.js";
import { go, setBackOverride } from "../router.js";
import { arenaLabel, cancelOnClose, otherSide, sendToTelegram, sendToWhatsApp, sendToX, stakeLabel } from "../challenge.js";

const TG = <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path d="M21.5 3.6 2.9 10.8c-1.2.5-1.2 1.2-.2 1.5l4.8 1.5 1.8 5.6c.2.6.4.8.9.8.4 0 .6-.2.9-.5l2.3-2.2 4.7 3.5c.9.5 1.5.2 1.7-.8l3.1-14.6c.3-1.3-.5-1.9-1.4-1.5Z" fill="#FFFFFF"></path></svg>;
const WA = <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.2a9.7 9.7 0 0 0-8.4 14.6L2.3 21.7l5-1.3A9.7 9.7 0 1 0 12 2.2Z" fill="#FFFFFF"></path><path d="M8.6 7.2c-.2-.5-.5-.5-.7-.5h-.6c-.2 0-.6.1-.9.4-.3.3-1.2 1.2-1.2 2.9s1.2 3.4 1.4 3.6c.2.2 2.4 3.8 5.9 5.2 2.9 1.1 3.5.9 4.1.9.6-.1 2-.8 2.3-1.6.3-.8.3-1.5.2-1.6-.1-.1-.3-.2-.7-.4l-2.3-1.1c-.3-.1-.5-.2-.8.2l-1 1.3c-.2.2-.4.3-.7.1-.4-.2-1.5-.6-2.8-1.8-1-.9-1.7-2-1.9-2.4-.2-.4 0-.6.2-.7l.5-.6c.2-.2.2-.4.4-.6.1-.2.1-.5 0-.7l-1.2-2.6Z" fill="#25D366"></path></svg>;
const XX = <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path d="M17.8 3h3.1l-6.8 7.7L22 21h-6.2l-4.9-6.4L5.3 21H2.2l7.2-8.3L1.8 3h6.4l4.4 5.8L17.8 3Zm-1.1 16.2h1.7L7.3 4.7H5.5l11.2 14.5Z" fill="#FFFFFF"></path></svg>;

function Target({ name, bg, icon, border, onClick }: { name: string; bg: string; icon: JSX.Element; border?: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{ flex: "1", display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", background: "none", border: "0", cursor: "pointer", color: "#FFFFFF", fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: "600", padding: "4px 0" }}>
      <span style={{ width: "50px", height: "50px", borderRadius: "16px", background: bg, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 20px rgba(0,0,0,0.35)", border: border ? "1px solid rgba(255,255,255,0.25)" : "0" }}>{icon}</span>{name}
    </button>
  );
}

export function App21b_ChallengeLive() {
  const { challenge, cancelChallenge, forgetChallenge, now } = useGame();
  useTicker(500);
  const [copied, setCopied] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const live = challenge?.kind === "link" && challenge.state === "open";
  const id = challenge?.id;

  // Nothing to wait for (a stale link, or the app reopened after it ended): set one up instead.
  useEffect(() => {
    if (!challenge || challenge.kind !== "link") go("App21", true);
  }, [!challenge]);

  const cancel = async (then: string) => {
    if (cancelling) return;
    setCancelling(true);
    setProblem(null);
    try {
      await cancelChallenge();
      // Taken before the cancel landed: the battle event has already moved the app on.
      if (location.hash === "#/App21b") go(then, true);
    } catch (e) {
      setProblem(e instanceof ApiError && e.code === "network" ? "No connection. Try again." : "Couldn't cancel. Try again.");
    } finally {
      setCancelling(false);
    }
  };

  // Back cancels while it waits; closing the app does too, as far as the phone lets us know.
  useEffect(() => {
    if (!live || !id) {
      setBackOverride(() => {
        forgetChallenge();
        go("App09", true);
      });
      return () => setBackOverride(null);
    }
    setBackOverride(() => void cancel("App09"));
    const onHide = () => cancelOnClose(id);
    addEventListener("pagehide", onHide);
    return () => {
      setBackOverride(null);
      removeEventListener("pagehide", onHide);
    };
  }, [live, id]);

  if (!challenge || challenge.kind !== "link") return null;
  const c = challenge;
  const stake = stakeLabel(c.stake);
  const left = Math.max(0, c.expiresAt - now());
  const clock = `${Math.floor(left / 60_000)}:${String(Math.floor((left % 60_000) / 1000)).padStart(2, "0")}`;
  const take = otherSide(c.side).toUpperCase();
  const copy = () => {
    if (!c.link) return;
    void navigator.clipboard?.writeText(c.link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  if (c.state !== "open") {
    const taken = c.state === "accepted";
    const title = taken ? "Taken! Starting your battle" : c.closeReason === "arena_locked" || c.closeReason === "arena_closed" ? "This arena closed" : c.state === "expired" ? "Nobody took it this time" : "Challenge cancelled";
    return (
      <div className="screen">
        <div className="orbL" aria-hidden="true" style={{ top: "40px", right: "-200px", width: "440px", height: "440px" }}></div>
        <div className="pad squeeze" style={{ height: "100%", paddingBottom: "calc(var(--dd-safe-bottom) + 8px)", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
          <h1 className="h1" style={{ marginTop: "40px", textAlign: "center" }}>{title}</h1>
          {!taken && (
            <div style={{ marginTop: "18px", display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", borderRadius: "14px", background: "rgba(204,255,0,0.08)", border: "1px solid rgba(204,255,0,0.25)" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#CCFF00" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7"></path></svg>
              <span style={{ fontSize: "13px", fontWeight: "600" }}>{c.stake} USDT is back in your balance</span>
            </div>
          )}
          {!taken && (
            <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: "10px" }}>
              <button className="btnP" onClick={() => { forgetChallenge(); go("App21", true); }}>MAKE ANOTHER</button>
              <button className="btnS" onClick={() => { forgetChallenge(); go("App09", true); }}>Back to the lobby</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="screen">
  <div className="orbL" aria-hidden="true" style={{ top: "40px", right: "-200px", width: "440px", height: "440px" }}></div>
  <div className="pad squeeze" style={{ height: "100%", paddingBottom: "calc(var(--dd-safe-bottom) + 8px)", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
    <div className="top" style={{ justifyContent: "center" }}><span className="chip chipL" style={{ gap: "6px" }}><span style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#CCFF00", boxShadow: "0 0 0 4px rgba(204,255,0,0.2)" }}></span>CHALLENGE LIVE</span></div>
    <h1 className="h1" style={{ marginTop: "0", textAlign: "center" }}>Waiting for a taker</h1>
    <p className="muted" style={{ margin: "6px 0 0", fontSize: "14px", textAlign: "center" }}>First to accept takes {take} and the battle starts at once.</p>

    <div style={{ marginTop: "18px", alignSelf: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
      <span className="lbl" style={{ fontSize: "10px" }}>OPEN FOR</span>
      <span className="mono lime" style={{ marginTop: "6px", fontWeight: "800", fontSize: "44px", lineHeight: "1" }}>{clock}</span>
    </div>

    <div className="g" style={{ marginTop: "14px", borderRadius: "18px", padding: "4px 0" }}>
      <div className="li"><span className="muted" style={{ flex: "1", fontSize: "13px" }}>Arena</span><span className="mono" style={{ fontSize: "13px", fontWeight: "700" }}>{arenaLabel(c.arena)}/USD · {c.durationSec === 60 ? "1 MIN BLITZ" : "3 MIN"}</span></div>
      <div className="div" style={{ margin: "0 14px" }}></div>
      <div className="li"><span className="muted" style={{ flex: "1", fontSize: "13px" }}>Your call</span>{c.side === "long" ? <span className="mono lime" style={{ fontSize: "13px", fontWeight: "700" }}>▲ LONG</span> : <span className="mono red" style={{ fontSize: "13px", fontWeight: "700" }}>▼ SHORT</span>}</div>
      <div className="div" style={{ margin: "0 14px" }}></div>
      <div className="li"><span className="muted" style={{ flex: "1", fontSize: "13px" }}>Stake locked</span><span className="mono" style={{ fontSize: "13px", fontWeight: "700" }}>{c.stake} USDT</span></div>
    </div>

    <button onClick={copy} className="g" style={{ marginTop: "12px", borderRadius: "14px", padding: "12px", display: "flex", alignItems: "center", gap: "10px", color: "#FFFFFF", cursor: "pointer", textAlign: "left", minHeight: "44px" }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#CCFF00" strokeWidth="2" strokeLinecap="round" style={{ flex: "none" }} aria-hidden="true"><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"></path><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"></path></svg>
      <span className="mono" style={{ flex: "1", minWidth: "0", fontSize: "12px", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{c.link?.replace(/^https:\/\//, "")}</span>
      <span className="lime" style={{ fontSize: "12px", fontWeight: "600", flex: "none" }}>{copied ? "Copied" : "Copy"}</span>
    </button>

    <div className="tallOnly" style={{ marginTop: "12px", display: "flex", gap: "10px", alignItems: "flex-start", padding: "10px 12px", borderRadius: "14px", background: "rgba(255,255,255,0.04)" }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B949E" strokeWidth="2.2" strokeLinecap="round" style={{ flex: "none", marginTop: "1px" }} aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 8v5M12 16v.5"></path></svg>
      <span className="muted" style={{ fontSize: "12px", lineHeight: "17px" }}>Stay on this screen. Switching to WhatsApp or X to send it is fine; going back or closing the app cancels it.</span>
    </div>

    <div style={{ marginTop: "auto", paddingTop: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
      <span className="lbl" style={{ textAlign: "center" }}>SEND IT TO</span>
      <div style={{ display: "flex", justifyContent: "space-around" }}>
        <Target name="Telegram" bg="#2AABEE" icon={TG} onClick={() => void sendToTelegram(c)} />
        <Target name="WhatsApp" bg="#25D366" icon={WA} onClick={() => sendToWhatsApp(c)} />
        <Target name="X" bg="#000000" icon={XX} border onClick={() => sendToX(c)} />
      </div>
      {problem && <span role="alert" className="red" style={{ textAlign: "center", fontSize: "12px" }}>{problem}</span>}
      <button className="btnS" style={{ color: "#FF5C85" }} disabled={cancelling} onClick={() => void cancel("App21")}>{cancelling ? "Cancelling..." : `Cancel challenge · get ${stake} USDT back`}</button>
    </div>
  </div>
</div>
    </>
  );
}
